import {useEffect, useState} from "react";
import {readFlowTrace, type FlowTraceNode, type FlowTraceView} from "../api/flowTrace";
import {useAuth} from "../auth/AuthContext";
import {AsyncState} from "../components/AsyncState";
import {PageHeader} from "../components/PageHeader";

export function FlowTrace({flowTraceId}: {flowTraceId: string}) {
  const {authorizedFetch} = useAuth();
  const [trace, setTrace] = useState<FlowTraceView | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setTrace(null);
    setError("");
    void readFlowTrace(authorizedFetch, flowTraceId, controller.signal)
      .then(result => { if (!controller.signal.aborted) setTrace(result); })
      .catch(problem => { if (!controller.signal.aborted) setError(message(problem)); });
    return () => controller.abort();
  }, [authorizedFetch, flowTraceId]);
  if (error) return <AsyncState kind="error" detail={error} />;
  if (!trace) return <AsyncState kind="loading" detail="Resolving governed flow evidence across current Factory surfaces." />;
  return <>
    <PageHeader eyebrow="Governed flow trace" title={trace.flowTraceId}
      description="One immutable identity across authority, workspace, supervised execution, Tools, evidence, audit and Execution Center." />
    <section className="panel page-panel">
      <p>This read-only projection is not approval or execution authority. Missing records are not proof of completion.</p>
      <dl className="preview-meta">{Object.entries(trace.surfaceCounts).map(([surface, count]) =>
        <div key={surface}><dt>{human(surface)}</dt><dd>{count}</dd></div>)}</dl>
      <div className="table-scroll" tabIndex={0} aria-label="Governed flow trace records">
        <table><thead><tr><th>Surface</th><th>Object</th><th>Key</th><th>State</th><th>Observed</th><th>Bound identities</th></tr></thead>
          <tbody>{trace.nodes.map(node => <TraceRow key={`${node.surface}:${node.objectId}`} node={node} />)}</tbody></table>
      </div>
    </section>
  </>;
}

function TraceRow({node}: {node: FlowTraceNode}) {
  const href = node.surface === "EXECUTION_CENTER" && node.objectType === "RUN"
    ? `/execution-center/${encodeURIComponent(node.objectId)}` : null;
  return <tr><td><strong>{human(node.surface)}</strong></td>
    <td>{human(node.objectType)}</td>
    <td>{href ? <a href={href}>{node.objectKey}</a> : <><code>{node.objectKey}</code><small>{node.objectId}</small></>}</td>
    <td><span className="badge">{human(node.state)}</span></td>
    <td>{new Date(node.observedAt).toLocaleString()}</td>
    <td><details><summary>Inspect identities</summary><pre>{JSON.stringify(node.details, null, 2)}</pre></details></td></tr>;
}

function human(value: string) { return value.replaceAll("_", " ").toLowerCase().replace(/^./, c => c.toUpperCase()); }
function message(problem: unknown) { return problem instanceof Error ? problem.message : "Governed flow trace failed"; }
